import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { WelcomeScreen } from './components/WelcomeScreen';
import { ThankYouScreen } from './components/ThankYouScreen';
import { ProgressBar } from './components/ProgressBar';
import { QuestionScreen, QuestionConfig } from './components/QuestionScreen';
import { BrandHeader } from './components/BrandHeader';
import { submitApplication } from './lib/submitApplication';
type Stage = 'welcome' | 'questions' | 'thanks';
type Answers = Record<string, string | boolean | string[] | null>;
const BASE_QUESTIONS: QuestionConfig[] = [{
  id: 'email',
  number: 1,
  type: 'email',
  question: "What's your email address?",
  helper: 'So we can get back to you',
  required: true,
  placeholder: 'name@email.com'
}, {
  id: 'designLink',
  number: 2,
  type: 'text',
  question: "Share a link to a Magic Patterns design you're proud of",
  helper: 'Something you would be happy for us to show off in socials',
  required: true,
  placeholder: 'https://magicpatterns.com/...'
}, {
  id: 'location',
  number: 3,
  type: 'text',
  question: 'Where are you based?',
  helper: 'City, Country',
  required: true,
  placeholder: 'San Francisco, USA'
}, {
  id: 'meetup',
  number: 4,
  type: 'yesno',
  question: 'Would you be open to organizing a Magic Patterns meetup in your city?',
  helper: "We'll cover the costs.",
  required: true
}, {
  id: 'twitter',
  number: 5,
  type: 'text',
  question: "What's your Twitter / X handle?",
  required: true,
  placeholder: '@yourhandle'
}, {
  id: 'linkedin',
  number: 6,
  type: 'text',
  question: "What's your LinkedIn?",
  required: true,
  placeholder: 'https://linkedin.com/in/...'
}, {
  id: 'sweaterSize',
  number: 7,
  type: 'multiselect',
  question: "What's your sweater size?",
  helper: 'Pick one. We send Magic Patterns swag to every Ambassador.',
  required: true,
  options: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
  single: true
}];
function isEmailValid(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}
function isUrlValid(v: string) {
  try {
    const u = new URL(v.trim());
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}
function getInitialStage(): Stage {
  if (typeof window === 'undefined') return 'welcome';
  const hash = window.location.hash.replace('#', '').toLowerCase();
  if (hash === 'thanks' || hash === 'thank-you') return 'thanks';
  if (hash === 'questions') return 'questions';
  return 'welcome';
}
export function App() {
  const [stage, setStage] = useState<Stage>(getInitialStage);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  useEffect(() => {
    const onHashChange = () => setStage(getInitialStage());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  const questions = useMemo(() => {
    return BASE_QUESTIONS.map((q, i) => ({
      ...q,
      number: i + 1
    }));
  }, []);
  const current = questions[stepIndex];
  const progress = stage === 'welcome' ? 0 : stage === 'thanks' ? 100 : stepIndex / questions.length * 100;
  const setAnswer = (id: string, v: string | boolean | string[]) => {
    setAnswers(a => ({
      ...a,
      [id]: v
    }));
    if (errors[id]) setErrors(e => ({
      ...e,
      [id]: null
    }));
    if (submitError) setSubmitError(null);
  };
  const validateCurrent = (): boolean => {
    if (!current) return true;
    const v = answers[current.id];
    if (current.required) {
      if (current.type === 'yesno') {
        if (v === null || v === undefined) {
          setErrors(e => ({
            ...e,
            [current.id]: 'Please select Yes or No.'
          }));
          return false;
        }
      } else if (current.type === 'multiselect') {
        if (!Array.isArray(v) || v.length === 0) {
          setErrors(e => ({
            ...e,
            [current.id]: 'Please select at least one option.'
          }));
          return false;
        }
      } else {
        if (typeof v !== 'string' || v.trim().length === 0) {
          setErrors(e => ({
            ...e,
            [current.id]: 'This field is required.'
          }));
          return false;
        }
      }
    }
    if (current.type === 'email' && typeof v === 'string' && v.trim().length > 0) {
      if (!isEmailValid(v)) {
        setErrors(e => ({
          ...e,
          [current.id]: 'Please enter a valid email address.'
        }));
        return false;
      }
    }
    if (current.id === 'designLink' && typeof v === 'string' && v.trim().length > 0) {
      if (!isUrlValid(v)) {
        setErrors(e => ({
          ...e,
          [current.id]: 'Please enter a full URL starting with https://'
        }));
        return false;
      }
    }
    if (current.id === 'linkedin' && typeof v === 'string' && v.trim().length > 0) {
      if (!isUrlValid(v)) {
        setErrors(e => ({
          ...e,
          [current.id]: 'Please enter a full URL starting with https://'
        }));
        return false;
      }
    }
    return true;
  };
  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    const sweater = Array.isArray(answers.sweaterSize) ? answers.sweaterSize[0] as string || '' : answers.sweaterSize as string || '';
    const result = await submitApplication({
      email: answers.email as string || '',
      designLink: answers.designLink as string || '',
      location: answers.location as string || undefined,
      meetup: answers.meetup === true,
      twitter: answers.twitter as string || undefined,
      linkedin: answers.linkedin as string || undefined,
      sweaterSize: sweater
    }, honeypot);
    setSubmitting(false);
    if (result.ok) {
      setAnswers({});
      setErrors({});
      setHoneypot('');
      setStepIndex(0);
      setStage('thanks');
    } else {
      setSubmitError(result.message);
    }
  };
  const handleNext = () => {
    if (!validateCurrent()) return;
    if (stepIndex < questions.length - 1) {
      setStepIndex(i => i + 1);
    } else {
      handleSubmit();
    }
  };
  const handleBack = () => {
    if (submitting) return;
    if (stepIndex > 0) {
      setStepIndex(i => i - 1);
    } else {
      setStage('welcome');
    }
  };
  const startQuestions = () => {
    setStage('questions');
    setStepIndex(0);
    setSubmitError(null);
  };
  return <div className="relative min-h-screen w-full bg-black text-white" data-id="element-0">
      {/* Subtle dot grid texture */}
      <div aria-hidden className="pointer-events-none fixed inset-0 opacity-[0.08]" style={{
      backgroundImage: 'radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)',
      backgroundSize: '24px 24px',
      maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
      WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)'
    }} data-id="element-1" />

      {stage === 'questions' && <BrandHeader data-id="element-2" />}
      {stage === 'questions' && <ProgressBar value={progress} data-id="element-3" />}

      <AnimatePresence mode="wait" data-id="element-4">
        {stage === 'welcome' && <WelcomeScreen key="welcome" onStart={startQuestions} data-id="element-5" />}

        {stage === 'questions' && current && <QuestionScreen key={current.id} config={current} value={answers[current.id] ?? (current.type === 'yesno' ? null : current.type === 'multiselect' ? [] : '')} onChange={v => setAnswer(current.id, v)} onNext={handleNext} onBack={handleBack} canGoBack={stepIndex > 0 || stage === 'questions'} error={errors[current.id]} isLast={stepIndex === questions.length - 1} submitting={submitting} submitError={submitError} honeypot={honeypot} onHoneypotChange={setHoneypot} data-id="element-6" />}

        {stage === 'thanks' && <ThankYouScreen key="thanks" data-id="element-7" />}
      </AnimatePresence>
    </div>;
}